import React, { useMemo } from 'react'

import { faSearch } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Box, CircularProgress, InputAdornment, TextField, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import cn from 'classnames'
import { isEmpty, omit } from 'lodash'
import PropTypes from 'prop-types'
import { nlpPlaceholderOptions } from './nlp-placeholder-options'

import { useIsNLPCollegeSearchEnabled } from 'store/feature-toggle/hooks'

import { useAwesomeBarContext } from '../../AwesomeBarContext'
import useEachOnInterval from './use-each-on-interval'

const INTERVAL_MS = 6000

const useStyles = makeStyles((theme) => {
  const placeholder = {
    color: 'inherit',
    opacity: 0.8,
  }
  const visible = { color: theme.palette.grey['600'] }
  const hidden = { color: 'transparent' }
  return {
    inputFrozen: visible,
    inputEmpty: {
      ...visible,
      animationName: '$fadeInOut',
      animationDuration: `${INTERVAL_MS}ms`,
      animationIterationCount: 'infinite',
      animationTimingFunction: 'ease-in',
    },
    textField: {
      '&:not(:focus-within) ::placeholder': placeholder /* Chrome, Firefox, Opera, Safari 10.1+ */,
      '&:not(:focus-within) :-ms-input-placeholder': placeholder /* Internet Explorer 10-11 */,
      '&:not(:focus-within) ::-ms-input-placeholder': placeholder /* Microsoft Edge */,
    },
    '@keyframes fadeInOut': {
      '0%': hidden,
      '15%': visible,
      '85%': visible,
      '100%': hidden,
    },
  }
})


/**
 * Text input used in college search
 * @param {object} props
 * @prop {bool} loading
 * @prop {object} InputProps
 * @prop {object} InputRef
 * @returns Input component
 */
const Input = ({ loading, InputProps, inputRef, ...rest }) => {
  const classes = useStyles()
  const props = omit(rest, ['staticContext', 'dispatch'])

  const isInputEmpty = isEmpty(rest.inputProps.value)

  const { placeholderOptions } = useAwesomeBarContext()
  const isNLPSearchEnabled = useIsNLPCollegeSearchEnabled()
  const effectivePlaceholderOptions = isNLPSearchEnabled ? nlpPlaceholderOptions : placeholderOptions
  const hasMoreThanOnePlaceholder = useMemo(() => effectivePlaceholderOptions.length > 1, [effectivePlaceholderOptions])
  const placeholder = useEachOnInterval(effectivePlaceholderOptions, INTERVAL_MS, !isInputEmpty)

  return (
    <TextField
      {...props}
      placeholder={placeholder}
      variant="outlined"
      inputRef={inputRef}
      inputProps={{ ...props.inputProps, 'aria-label': 'Search' }}
      InputProps={{
        ...omit(InputProps, ['staticContext', 'dispatch']),
        className: cn(classes.textField, {
          [classes.inputEmpty]: hasMoreThanOnePlaceholder && isInputEmpty,
          [classes.inputFrozen]: !hasMoreThanOnePlaceholder,
        }),
        endAdornment: (
          <React.Fragment>
            {loading ? (
              <CircularProgress
                color="inherit"
                size={16}
              />
            ) : null}
            {InputProps.endAdornment}
          </React.Fragment>
        ),
        startAdornment: (
          <InputAdornment position="start">
            <Box marginLeft={1}>
              <Typography color="textSecondary">
                <FontAwesomeIcon icon={faSearch} />
              </Typography>
            </Box>
          </InputAdornment>
        ),
      }}
    />
  )
}

Input.propTypes = {
  InputProps: PropTypes.object,
  loading: PropTypes.bool,
  inputRef: PropTypes.object,
}

export default Input
